<template>
	<div class="navigation-filters">
		<MobileFilter
			v-if="hasMobileFilters"
			ref="mobileFilter"
			:filters="mobileFilters"
			@changeSelect="handleMobileFilterChange"
			@reset="resetMobileFilters"
		/>

		<div
			v-if="hasMobileFilters"
			class="navigation-filters__mobile-filter-toggle"
			:class="{'is-filtered': mobileFilterIsFiltered, '-extra-top-offset': filterToggleExtraTopOffset}"
			@click="openMobileFilter"
		>
			<Icon
				icon-name="filter"
				size="lg"
			/>
		</div>

		<div class="navigation-filters__desktop">
			<div
				v-if="filters.length > 0"
				class="navigation-filters__grid"
				:style="{ '--search-span': inlineSearchSpan }"
			>
				<Select
					v-for="filter in filters"
					:key="filter.key"
					class="navigation-filters__select"
					:options="filter.options"
					:value="filter.value"
					:searchable="true"
					:clearable="false"
					:dark-theme="!$arenaConfig.isLightTheme"
					:rounded="true"
					@change="$emit('filter-change', { key: filter.key, value: $event })"
				/>

				<InputText
					v-if="shouldInlineSearch"
					class="navigation-filters__search -inline"
					:dark-theme="!$arenaConfig.isLightTheme"
					type="search"
					:placeholder="searchPlaceholder"
					:value="localSearchValue"
					:rounded="true"
					@input="handleSearchInput"
				/>
			</div>

			<InputText
				v-if="showSearch && !shouldInlineSearch"
				class="navigation-filters__search -full"
				:dark-theme="!$arenaConfig.isLightTheme"
				type="search"
				:placeholder="searchPlaceholder"
				:value="localSearchValue"
				:rounded="true"
				@input="handleSearchInput"
			/>
		</div>

		<InputText
			v-if="showSearch"
			class="navigation-filters__search -full u-hide@md"
			:dark-theme="!$arenaConfig.isLightTheme"
			type="search"
			:placeholder="searchPlaceholder"
			:value="localSearchValue"
			:rounded="true"
			@input="handleSearchInput"
		/>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon';
import MobileFilter from '@js/components/MobileFilter';
import InputText from '@shared/components/Form/InputText';
import Select from '@shared/components/Select';
import { debounce } from '@shared/utils/debounce';

export default {
	name:       'NavigationFilters',
	components: {
		Icon,
		InputText,
		MobileFilter,
		Select,
	},
	props: {
		filters: {
			type:    Array,
			default: () => [],
		},
		showSearch: {
			type:    Boolean,
			default: true,
		},
		searchValue: {
			type:    String,
			default: '',
		},
		extraMobileFilters: {
			type:    Array,
			default: () => [],
		},
		filterToggleExtraTopOffset: {
			type:    Boolean,
			default: false,
		},
	},
	emits: ['search-change', 'filter-change'],
	data() {
		return {
			debouncedSearchChange: null,
			localSearchValue:      this.searchValue,
		};
	},
	computed: {
		searchPlaceholder() {
			return this.$Helpers.ucfirst(this.$I18n.trans('commons.search'));
		},
		mobileFilters() {
			const resolvedFilters = this.filters.map(filter => ({
				type:         'select',
				id:           filter.key,
				label:        filter.label || filter.options?.[0]?.label || filter.key,
				defaultValue: filter.defaultValue ?? filter.options?.[0]?.value ?? null,
				value:        filter.value ?? filter.defaultValue ?? filter.options?.[0]?.value ?? null,
				options:      filter.options || [],
			}));

			return [
				...this.extraMobileFilters,
				...resolvedFilters,
			];
		},
		hasMobileFilters() {
			return this.mobileFilters.length > 0;
		},
		mobileFilterIsFiltered() {
			return this.mobileFilters.some(filter => filter.value !== filter.defaultValue);
		},
		inlineSearchSpan() {
			const remainder = this.filters.length % 4;
			if (remainder === 0) {
				return 1;
			}

			return 4 - remainder;
		},
		shouldInlineSearch() {
			if (!this.showSearch) {
				return false;
			}

			const activeFilterCount = this.filters.length;
			if (activeFilterCount <= 0) {
				return false;
			}

			return activeFilterCount % 4 !== 0;
		},
	},
	watch: {
		searchValue(value) {
			this.localSearchValue = value || '';
		},
	},
	created() {
		// Debounce query updates so typing does not re-run filtering and URL state on every keypress.
		this.debouncedSearchChange = debounce(this.emitSearchChange, 300, false);
	},
	methods: {
		handleSearchInput(valueOrEvent) {
			const nextValue = typeof valueOrEvent === 'string'
				? valueOrEvent
				: valueOrEvent?.target?.value || '';

			this.localSearchValue = nextValue;
			this.debouncedSearchChange(nextValue);
		},
		emitSearchChange(value) {
			this.$emit('search-change', value);
		},
		openMobileFilter() {
			this.$refs.mobileFilter?.open();
		},
		handleMobileFilterChange(select, value) {
			const key = select?.id;
			if (!key) {
				return;
			}

			this.$emit('filter-change', { key, value });
		},
		resetMobileFilters() {
			this.mobileFilters.forEach(filter => {
				this.$emit('filter-change', {
					key:   filter.id,
					value: filter.defaultValue,
				});
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.navigation-filters__desktop {
	display: none;

	@include size(md-up) {
		display: block;
	}
}

.navigation-filters__grid {
	display: none;

	@include size(md-up) {
		display: grid;
		gap: $sp2;
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
}

.navigation-filters__search {
	&.-inline {
		grid-column: span var(--search-span);
		min-width: 0;
	}

	&.-full {
		width: 100%;
	}
}

.navigation-filters__mobile-filter-toggle {
	position: absolute;
	right: 0;
	top: $sp2;
	display: flex;
	align-items: center;
	cursor: pointer;

	&.is-filtered {
		color: var(--color-hype-yellow);
	}

	&.-extra-top-offset {
		top: 10px; // stylelint-disable-line
	}

	@include size(md-up) {
		display: none;
	}
}

@include size(sm-only) {
	.navigation-filters__search.-inline {
		grid-column: span 1;
	}
}
</style>
